import * as React from "react";

import { getRTL } from "@fluentui/react/lib/Utilities";

import {
  AppPaddleButton,
  appPaddleWidth,
  PaddleOrientation,
} from "../AppPaddleButton";
import type { AppAutoOverflowPaddleControlProps } from "./AppOverflowPaddleControl.Props";
import { performOnNextFrame } from "@1js/acui-common";
import {
  leftPaddleId,
  leftPaddleSelector,
  rightPaddleId,
  rightPaddleSelector,
} from "./AppOverflowPaddleConstants";
import { AppContext } from "@1js/acui-context";

export interface AppAutoOverflowPaddleControlState {
  paddleOrientation: PaddleOrientation;
}

export class AppAutoOverflowPaddleControl extends React.PureComponent<
  React.PropsWithChildren<AppAutoOverflowPaddleControlProps>,
  AppAutoOverflowPaddleControlState
> {
  // This allows us to access AppContext directly from this.context
  static contextType: any = AppContext;
  declare context: React.ContextType<typeof AppContext>;

  private container = React.createRef<HTMLDivElement>();
  private intersectionObserver: IntersectionObserver | undefined = undefined;

  // Number of pixels we will allow the calculations to be off by.
  private paddlePixelThreshold = 1;
  private isRTL = false;
  private leftPaddleLabel = "";
  private rightPaddleLabel = "";
  private amountOverflowing = 0;
  private appliedOffsetAmount = 0;
  private delayRenderId = -1;

  constructor(props: AppAutoOverflowPaddleControlProps) {
    super(props);
    this.isRTL = getRTL();
    if (this.isRTL) {
      this.leftPaddleLabel = this.props.rightRibbonPaddleLabel;
      this.rightPaddleLabel = this.props.leftRibbonPaddleLabel;
    } else {
      this.leftPaddleLabel = this.props.leftRibbonPaddleLabel;
      this.rightPaddleLabel = this.props.rightRibbonPaddleLabel;
    }

    this.state = {
      paddleOrientation: PaddleOrientation.Hidden,
    };
  }

  private onIntersectionObserved = (entries: IntersectionObserverEntry[]) => {
    try {
      const entry = entries[entries.length - 1];

      if (entry.intersectionRatio < 1) {
        /* We are overflowing */

        // Grab the size (in px) that the content is overflowing
        this.amountOverflowing =
          entry.boundingClientRect.width -
          entry.boundingClientRect.width * entry.intersectionRatio;

        // Make the paddles visible (during idle) if they are not already
        if (!this.isPaddlesVisible()) {
          // Make sure we have at most one render queued
          this.cancelPendingRender();

          const handleChildOverflowCallback = () =>
            this.handleChildOverflow(
              this.amountOverflowing,
              entry.boundingClientRect.width
            );

          this.delayRenderId =
            !!window.requestIdleCallback && !this.props.expediteOverflowHandling
              ? (window.requestIdleCallback(
                  handleChildOverflowCallback
                ) as any) /* needed so typescript will not complain */
              : performOnNextFrame(handleChildOverflowCallback);
        }
      } else if (entry.intersectionRatio === 1) {
        /* We are no longer overflowing */

        // Cancel any pending render that was scheduled
        this.cancelPendingRender();

        this.amountOverflowing = 0;

        if (this.appliedOffsetAmount && this.container.current) {
          this.container.current.style.transform = "translateX(0px)";
        }
        this.appliedOffsetAmount = 0;

        if (this.isPaddlesVisible()) {
          this.handleChildOverflow();
        }
      }
    } catch (exception: any) {
      const message =
        exception instanceof Error
          ? exception.message
          : typeof exception === "string"
            ? exception
            : "unknown";
      this.props.onError?.(
        `Error in AppAutoOverflowPaddleControl.onIntersectionObserved with message: ${message}`
      );
    }
  };

  public componentDidMount = () => {
    if (!this.container.current?.children?.length) {
      return;
    }

    try {
      this.intersectionObserver = new IntersectionObserver(
        this.onIntersectionObserved,
        {
          root: this.container.current,
          threshold: [
            0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1,
          ] /* call the callback for every 10% change in overflow */,
        }
      );

      this.intersectionObserver.observe(this.container.current.children[0]);
    } catch (exception: any) {
      const message =
        exception instanceof Error
          ? exception.message
          : typeof exception === "string"
            ? exception
            : "unknown";
      this.props.onError?.(
        `Error in AppAutoOverflowPaddleControl.componentDidMount with message: ${message}`
      );
    }
  };

  public componentWillUnmount = () => {
    this.cancelPendingRender();
    try {
      this.intersectionObserver?.disconnect();
    } catch (exception: any) {
      const message =
        exception instanceof Error
          ? exception.message
          : typeof exception === "string"
            ? exception
            : "unknown";
      this.props.onError?.(
        `Error in AppAutoOverflowPaddleControl.componentWillUnmount with message: ${message}`
      );
    }
  };

  private cancelPendingRender() {
    if (this.delayRenderId !== -1) {
      (window.requestIdleCallback as any) /* needed so typescript will not complain */
        ? window.cancelIdleCallback(this.delayRenderId)
        : window.cancelAnimationFrame(this.delayRenderId);
    }
  }

  public render() {
    const { downChevronIcon, measuringOffset } = this.props;

    return (
      <div id="paddleContainer">
        <div
          ref={this.container}
          style={{
            width: measuringOffset
              ? `calc(100% - ${measuringOffset}px)`
              : "100%",
            display: "inline-flex",
            position: "absolute",
            willChange: "transform",
          }}
        >
          {this.props.children}
        </div>
        {(this.state.paddleOrientation === PaddleOrientation.Right ||
          this.state.paddleOrientation === PaddleOrientation.LeftRight) && (
          <AppPaddleButton
            id={rightPaddleId}
            icon={downChevronIcon}
            orientation={PaddleOrientation.Right}
            onExecute={this.onPaddleButtonExecute}
            label={this.rightPaddleLabel}
            disableModeTogglePadding={this.props.disableModeTogglePadding}
          />
        )}
        {(this.state.paddleOrientation === PaddleOrientation.Left ||
          this.state.paddleOrientation === PaddleOrientation.LeftRight) && (
          <AppPaddleButton
            id={leftPaddleId}
            icon={downChevronIcon}
            orientation={PaddleOrientation.Left}
            onExecute={this.onPaddleButtonExecute}
            label={this.leftPaddleLabel}
            disableModeTogglePadding={this.props.disableModeTogglePadding}
          />
        )}
      </div>
    );
  }

  public componentDidUpdate(
    _prevProps: AppAutoOverflowPaddleControlProps,
    prevState: AppAutoOverflowPaddleControlState
  ) {
    if (
      prevState.paddleOrientation === PaddleOrientation.Hidden &&
      this.state.paddleOrientation !== PaddleOrientation.Hidden
    ) {
      this.props.onPaddlesShown?.();
    }
  }

  public isPaddlesVisible = () => {
    return this.state.paddleOrientation !== PaddleOrientation.Hidden;
  };

  public isEndPaddleVisible = () => {
    return (
      this.state.paddleOrientation === PaddleOrientation.Right ||
      this.state.paddleOrientation === PaddleOrientation.LeftRight
    );
  };

  public hideRibbonPaddles = () => {
    this.handleChildOverflow();
  };

  /**
   * Function that handles determining if we should add paddles to the ribbon
   */
  public handleChildOverflow = (
    overflowingAmount?: number,
    renderedWidth?: number
  ) => {
    if (
      !!overflowingAmount &&
      !!renderedWidth &&
      !!this.props.onContentOverflow?.(renderedWidth, overflowingAmount)
    ) {
      // The orchestrator has taken steps to shrink the rendered content
      return;
    }

    if (
      !overflowingAmount &&
      this.state.paddleOrientation !== PaddleOrientation.Hidden
    ) {
      this.amountOverflowing = 0;
      this.appliedOffsetAmount = 0;
      if (this.container.current) {
        this.container.current.style.transform = "translateX(0px)";
      }

      this.setState({ paddleOrientation: PaddleOrientation.Hidden });
      return;
    }

    if (this.isRTL) {
      this.handleChildOverflowRightToLeft();
    } else {
      this.handleChildOverflowLeftToRight();
    }
  };

  /**
   * Allow the browser to scroll element into view, and then
   * readjust the applied offset if the focused element
   * will still be partially obscured by the paddles.
   */
  public scrollActiveElementIntoView = (activeElement: HTMLElement) => {
    activeElement.scrollIntoView({ block: "nearest" });

    if (!this.container.current) return;
    const elementRect = activeElement.getBoundingClientRect();
    const orientation = this.getPaddleOriginationBasedOnRTL();

    // Check if "left" paddle is blocking
    if (
      orientation === PaddleOrientation.Left ||
      orientation === PaddleOrientation.LeftRight
    ) {
      const paddleQuerySelector = this.isRTL
        ? rightPaddleSelector
        : leftPaddleSelector;
      const lPaddle = document.querySelector(paddleQuerySelector);
      if (lPaddle) {
        const paddleRect = lPaddle.getBoundingClientRect();
        const paddleRightCorner = paddleRect.x + paddleRect.width;
        if (elementRect.x < paddleRightCorner) {
          this.appliedOffsetAmount += paddleRightCorner - elementRect.x;
          this.container.current.style.transform = `translateX(${this.appliedOffsetAmount}px)`;
          return;
        }
      }
    }

    // Check if "right" paddle is blocking
    if (
      orientation === PaddleOrientation.Right ||
      orientation === PaddleOrientation.LeftRight
    ) {
      const paddleQuerySelector = this.isRTL
        ? leftPaddleSelector
        : rightPaddleSelector;
      const rPaddle = document.querySelector(paddleQuerySelector);
      if (rPaddle) {
        const paddleRect = rPaddle.getBoundingClientRect();
        if (paddleRect.x < elementRect.x + elementRect.width) {
          this.appliedOffsetAmount -=
            elementRect.x + elementRect.width - paddleRect.x;
          this.container.current.style.transform = `translateX(${this.appliedOffsetAmount}px)`;
        }
      }
    }
  };

  private handleChildOverflowRightToLeft = () => {
    // Ribbon paddles will be add if there is content (scrollWidth) outside of what can be seen (clientWidth).
    // If we have the scrollWidth-scrollLeft, which is > 0 in RTL if any content is off screen
    // is less than the clientWidth than we just need to add a paddle to the left.
    // If we have some amount of content off the screen to the left, but the width is
    // still greater than the clientWidth we need to add a paddle on both sides.  However, if the scrollLeft
    // is less than the pixels of the paddle, we will scroll over the additional amount to avoid
    // a control being slightly cut off.
    // Else we add it to just the left
    if (!this.container.current) {
      return;
    }
    if (
      this.container.current.scrollWidth - this.appliedOffsetAmount <=
      this.container.current.clientWidth + this.paddlePixelThreshold
    ) {
      this.setState({ paddleOrientation: PaddleOrientation.Left });
    } else if (
      this.container.current.scrollWidth - this.appliedOffsetAmount >
      this.container.current.clientWidth
    ) {
      // If the content that remains off screener is less than the size of the paddle, scroll
      // it into view.
      if (this.appliedOffsetAmount < appPaddleWidth) {
        this.appliedOffsetAmount = 0;
        this.container.current.style.transform = "translateX(0px)";
        this.setState({ paddleOrientation: PaddleOrientation.Right });
        return;
      }
      this.setState({ paddleOrientation: PaddleOrientation.LeftRight });
    } else {
      this.setState({ paddleOrientation: PaddleOrientation.Right });
    }
  };

  private handleChildOverflowLeftToRight = () => {
    // Ribbon paddles will be add if there is content (scrollWidth) outside of what can be seen (clientWidth).
    // If we have the scrollWidth is greater than the clientWidth, but the scrollLeft is 0,
    // than we just need to add a paddle to the right.
    // If we have some amount of content off the screen to the left, but we the width is
    // still greater than the clientWidth we need to add a paddle on both sides.
    // Else we add it to just the left
    // If the scrollWidth is less than or equal to the clientWidth the paddles will be hidden
    if (!this.container.current) {
      return;
    }
    if (Math.abs(this.appliedOffsetAmount) < appPaddleWidth) {
      // Make sure scrollLeft is 0 if only showing the Right paddle.
      this.appliedOffsetAmount = 0;
      this.container.current.style.transform = "translateX(0px)";

      this.setState({ paddleOrientation: PaddleOrientation.Right });
    } else if (
      this.container.current.scrollWidth - Math.abs(this.appliedOffsetAmount) >
      this.container.current.clientWidth
    ) {
      // If the content that remains off screener is less than the size of the paddle, scroll
      // it into view.
      if (
        this.container.current.scrollWidth -
          Math.abs(this.appliedOffsetAmount) -
          this.container.current.clientWidth <=
        appPaddleWidth
      ) {
        this.appliedOffsetAmount += appPaddleWidth;
        this.container.current.style.transform = `translateX(${this.appliedOffsetAmount}px)`;
        this.setState({ paddleOrientation: PaddleOrientation.Left });
        return;
      }

      this.setState({ paddleOrientation: PaddleOrientation.LeftRight });
    } else {
      this.setState({ paddleOrientation: PaddleOrientation.Left });
    }
  };

  private getPaddleOriginationBasedOnRTL() {
    if (this.isRTL) {
      switch (this.state.paddleOrientation) {
        case PaddleOrientation.Right:
          return PaddleOrientation.Left;
        case PaddleOrientation.Left:
          return PaddleOrientation.Right;
        case PaddleOrientation.LeftRight:
          return PaddleOrientation.LeftRight;
        default:
          return PaddleOrientation.Hidden;
      }
    }

    return this.state.paddleOrientation;
  }

  /**
   * Function to handle execute of ribbon paddles.
   */
  private onPaddleButtonExecute = (id: string) => {
    this.props.onPaddleExecuted?.(id);

    if (this.container.current) {
      const paddleOrientation = this.state.paddleOrientation;
      const unseenContentWidthSinglePaddle = this.amountOverflowing;
      const unseenContentWidthDoublePaddle =
        this.amountOverflowing - Math.abs(this.appliedOffsetAmount);

      if (paddleOrientation === PaddleOrientation.LeftRight) {
        // If the current state is that both paddles are displayed and the right paddle is executed.
        // If the width of the content that isn't seen by the user is less than the clientWidth, we can
        // scroll to the end and show just the left paddle
        // Otherwise we need to scroll the ribbon to the right the width of the view area and keep
        // both paddles
        // This is the same but in reverse if the LeftPaddle is being executed
        if (unseenContentWidthDoublePaddle === 0) {
          this.setState({ paddleOrientation: PaddleOrientation.Hidden });
          return;
        }

        if (id === rightPaddleId) {
          if (
            unseenContentWidthDoublePaddle <= this.container.current.clientWidth
          ) {
            this.appliedOffsetAmount = this.isRTL
              ? this.appliedOffsetAmount + unseenContentWidthDoublePaddle
              : this.appliedOffsetAmount - unseenContentWidthDoublePaddle;
            this.container.current.style.transform = `translateX(${this.appliedOffsetAmount}px)`;
            this.setState({ paddleOrientation: PaddleOrientation.Left });
          } else {
            this.appliedOffsetAmount = this.isRTL
              ? this.appliedOffsetAmount + this.container.current.clientWidth
              : this.appliedOffsetAmount - this.container.current.clientWidth;
            this.container.current.style.transform = `translateX(${this.appliedOffsetAmount}px)`;
          }
        } else {
          if (
            unseenContentWidthDoublePaddle <=
            this.container.current.clientWidth + this.paddlePixelThreshold
          ) {
            this.appliedOffsetAmount = 0;
            this.container.current.style.transform = `translateX(${this.appliedOffsetAmount}px)`;
            this.setState({ paddleOrientation: PaddleOrientation.Right });
          } else {
            this.appliedOffsetAmount = this.isRTL
              ? this.appliedOffsetAmount - this.container.current.clientWidth
              : this.appliedOffsetAmount + this.container.current.clientWidth;
          }
        }
      } else if (paddleOrientation === PaddleOrientation.Right) {
        // If the right paddle has been executed, and the content that is off screen is less
        // than the viewable width then we can scroll the width of the content unseen and show just
        // the left paddle.  If the content is greater, then we are in a middle state and need to
        // show both paddles.
        if (unseenContentWidthSinglePaddle === 0) {
          this.setState({ paddleOrientation: PaddleOrientation.Hidden });
          return;
        }

        if (
          unseenContentWidthSinglePaddle <= this.container.current.clientWidth
        ) {
          this.appliedOffsetAmount = this.isRTL
            ? this.appliedOffsetAmount + unseenContentWidthSinglePaddle
            : this.appliedOffsetAmount - unseenContentWidthSinglePaddle;
          this.container.current.style.transform = `translateX(${this.appliedOffsetAmount}px)`;
          this.setState({ paddleOrientation: PaddleOrientation.Left });
        } else {
          this.appliedOffsetAmount = this.isRTL
            ? this.container.current.clientWidth
            : -this.container.current.clientWidth;
          this.container.current.style.transform = `translateX(${this.appliedOffsetAmount}px)`;
          this.setState({ paddleOrientation: PaddleOrientation.LeftRight });
        }
      } else {
        // Else if the left paddle was executed follow similar logic to the right.
        if (unseenContentWidthSinglePaddle === 0) {
          this.setState({ paddleOrientation: PaddleOrientation.Hidden });
          return;
        }

        if (
          unseenContentWidthSinglePaddle <= this.container.current.clientWidth
        ) {
          this.appliedOffsetAmount = 0;
          this.container.current.style.transform = `translateX(${this.appliedOffsetAmount}px)`;
          this.setState({ paddleOrientation: PaddleOrientation.Right });
        } else {
          this.appliedOffsetAmount = this.isRTL
            ? this.appliedOffsetAmount - this.container.current.clientWidth
            : this.appliedOffsetAmount + this.container.current.clientWidth;
          this.container.current.style.transform = `translateX(${this.appliedOffsetAmount}px)`;
          this.setState({ paddleOrientation: PaddleOrientation.LeftRight });
        }
      }
    }
  };
}
